<template>
  <div v-if="show" class="crop-modal-overlay" @click.self="cancel">
    <div class="crop-modal-content">
      <!-- ========================================== -->
      <!-- هدر -->
      <!-- ========================================== -->
      <div class="crop-modal-header">
        <h3>✂️ برش عکس</h3>
        <button class="btn-close" @click="cancel">✕</button>
      </div>

      <!-- ========================================== -->
      <!-- ناحیه کراپ -->
      <!-- ========================================== -->
      <div class="crop-modal-body">
        <div class="crop-info-bar">
          <span v-if="scaleLabel" class="crop-info">
            نسبت: {{ scaleLabel }}
          </span>
          <span class="crop-info">
            ابعاد خروجی: {{ displayOutputWidth }}×{{ displayOutputHeight }}
          </span>
        </div>

        <div ref="cropContainer" class="crop-container">
          <img
              ref="sourceImage"
              :src="imageSrc"
              class="crop-source-image"
              @load="onImageLoad"
              draggable="false"
          />

          <!-- کادر کراپ -->
          <div
              v-if="cropBox.width > 0"
              class="crop-box"
              :style="cropBoxStyle"
              @mousedown.stop="startDrag"
              @touchstart.stop.prevent="startDrag"
          >
            <div
                v-for="handle in handles"
                :key="handle"
                :class="['crop-handle', 'handle-' + handle]"
                @mousedown.stop="startHandleResize(handle, $event)"
                @touchstart.stop.prevent="startHandleResize(handle, $event)"
            ></div>

            <div class="crop-grid">
              <div class="grid-line grid-v1"></div>
              <div class="grid-line grid-v2"></div>
              <div class="grid-line grid-h1"></div>
              <div class="grid-line grid-h2"></div>
            </div>
          </div>
        </div>

        <div class="crop-controls">
          <div class="zoom-controls">
            <button class="btn-zoom" @click="zoomOut" title="زوم اوت">🔍−</button>
            <span class="zoom-value">{{ Math.round(zoom * 100) }}%</span>
            <button class="btn-zoom" @click="zoomIn" title="زوم این">🔍+</button>
            <button class="btn-zoom btn-reset-zoom" @click="resetZoom" title="بازنشانی">↺</button>
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- فوتر -->
      <!-- ========================================== -->
      <div class="crop-modal-footer">
        <button class="btn-cancel" @click="cancel">انصراف</button>
        <button class="btn-confirm" @click="confirm">✅ تایید و آپلود</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CropModal',

  props: {
    show: { type: Boolean, default: false },
    imageSrc: { type: String, default: '' },
    cropScale: { type: String, default: null },
    maxWidth: { type: Number, default: null },
    maxHeight: { type: Number, default: null }
  },

  data() {
    return {
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      imageDisplayWidth: 0,
      imageDisplayHeight: 0,
      imageDisplayX: 0,   // ← اضافه شود
      imageDisplayY: 0,   // ← اضافه شود
      zoom: 1,
      minZoom: 0.5,
      maxZoom: 3,

      cropBox: { x: 0, y: 0, width: 0, height: 0 },

      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartBoxX: 0,
      dragStartBoxY: 0,

      isResizing: false,
      resizeHandle: null,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartBox: null,

      handles: ['nw', 'ne', 'sw', 'se'],
      minCropSize: 40
    };
  },

  computed: {
    scaleLabel() {
      return this.cropScale || null;
    },

    // ===== نسبت عددی =====
    scaleRatio() {
      if (!this.cropScale) return null;
      const parts = this.cropScale.split(':');
      if (parts.length !== 2) return null;
      const w = parseFloat(parts[0]);
      const h = parseFloat(parts[1]);
      if (!w || !h) return null;
      return w / h;
    },

    // ===== ابعاد خروجی (نمایش) =====
    displayOutputWidth() {
      return this.calculateOutputSize().width;
    },

    displayOutputHeight() {
      return this.calculateOutputSize().height;
    },

    cropBoxStyle() {
      return {
        left: (this.imageDisplayX + this.cropBox.x) + 'px',   // ← آفست X
        top: (this.imageDisplayY + this.cropBox.y) + 'px',    // ← آفست Y
        width: this.cropBox.width + 'px',
        height: this.cropBox.height + 'px'
      };
    },
  },

  watch: {
    show(val) {
      if (val && this.imageSrc) {
        this.zoom = 1;
        this.$nextTick(() => this.initCrop());
      }
    }
  },

  mounted() {
    this.bindGlobalEvents();
  },

  beforeDestroy() {
    this.unbindGlobalEvents();
  },

  methods: {
    // ==========================================
    // مقداردهی اولیه
    // ==========================================
    initCrop() {
      const container = this.$refs.cropContainer;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      this.imageDisplayWidth = rect.width;
      this.imageDisplayHeight = rect.height;

      this.resetCropBox();
    },

    onImageLoad() {
      const img = this.$refs.sourceImage;
      if (!img) return;

      this.imageNaturalWidth = img.naturalWidth;
      this.imageNaturalHeight = img.naturalHeight;

      const container = this.$refs.cropContainer;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const cw = rect.width;
      const ch = rect.height;

      // ==========================================
      // محاسبه محدوده واقعی تصویر (object-fit: contain)
      // ==========================================
      const imgRatio = this.imageNaturalWidth / this.imageNaturalHeight;
      const containerRatio = cw / ch;

      let displayW, displayH, offsetX, offsetY;

      if (imgRatio > containerRatio) {
        // تصویر پهن‌تر است → به عرض کانتینر می‌چسبد، نوار بالا/پایین
        displayW = cw;
        displayH = cw / imgRatio;
        offsetX = 0;
        offsetY = (ch - displayH) / 2;
      } else {
        // تصویر بلندتر است → به ارتفاع کانتینر می‌چسبد، نوار چپ/راست
        displayH = ch;
        displayW = ch * imgRatio;
        offsetX = (cw - displayW) / 2;
        offsetY = 0;
      }

      this.imageDisplayWidth = displayW;
      this.imageDisplayHeight = displayH;
      this.imageDisplayX = offsetX;
      this.imageDisplayY = offsetY;

      this.$nextTick(() => this.resetCropBox());
    },
    // ==========================================
    // کادر اولیه با نسبت دقیق
    // ==========================================
    resetCropBox() {
      const cw = this.imageDisplayWidth;
      const ch = this.imageDisplayHeight;

      if (!cw || !ch) return;

      let boxW = cw * 0.85;
      let boxH = ch * 0.85;

      // اعمال نسبت
      if (this.scaleRatio) {
        if (boxW / boxH > this.scaleRatio) {
          boxW = boxH * this.scaleRatio;
        } else {
          boxH = boxW / this.scaleRatio;
        }
      }

      // مرکز تصویر (نه کانتینر)
      this.cropBox = {
        x: (cw - boxW) / 2,
        y: (ch - boxH) / 2,
        width: boxW,
        height: boxH
      };
    },
    // ==========================================
    // زوم
    // ==========================================
    zoomIn() {
      this.zoom = Math.min(this.maxZoom, this.zoom + 0.1);
    },

    zoomOut() {
      this.zoom = Math.max(this.minZoom, this.zoom - 0.1);
    },

    resetZoom() {
      this.zoom = 1;
      this.resetCropBox();
    },

    // ==========================================
    // درگ کادر
    // ==========================================
    startDrag(e) {
      e.preventDefault();
      e.stopPropagation();
      const p = this.getPoint(e);
      this.isDragging = true;
      this.dragStartX = p.x;
      this.dragStartY = p.y;
      this.dragStartBoxX = this.cropBox.x;
      this.dragStartBoxY = this.cropBox.y;
    },

    onDrag(e) {
      if (!this.isDragging) return;
      e.preventDefault();

      const p = this.getPoint(e);
      const dx = p.x - this.dragStartX;
      const dy = p.y - this.dragStartY;

      this.cropBox.x = this.clamp(
          this.dragStartBoxX + dx, 0,
          this.imageDisplayWidth - this.cropBox.width
      );
      this.cropBox.y = this.clamp(
          this.dragStartBoxY + dy, 0,
          this.imageDisplayHeight - this.cropBox.height
      );
    },

    stopDrag() {
      this.isDragging = false;
    },

    // ==========================================
    // تغییر اندازه (فقط یک بعد + محاسبه دیگری از نسبت)
    // ==========================================
    startHandleResize(handle, e) {
      e.preventDefault();
      e.stopPropagation();
      const p = this.getPoint(e);
      this.isResizing = true;
      this.resizeHandle = handle;
      this.resizeStartX = p.x;
      this.resizeStartY = p.y;
      this.resizeStartBox = { ...this.cropBox };
    },

    onResize(e) {
      if (!this.isResizing) return;
      e.preventDefault();

      const p = this.getPoint(e);
      const dx = p.x - this.resizeStartX;
      const dy = p.y - this.resizeStartY;

      const box = this.resizeStartBox;
      const min = this.minCropSize;
      const ratio = this.scaleRatio;

      let newX = box.x;
      let newY = box.y;
      let newW = box.width;
      let newH = box.height;

      // ==========================================
      // تعیین تغییر بر اساس گوشه
      // ==========================================
      const rightEdge = box.x + box.width;
      const bottomEdge = box.y + box.height;

      if (ratio) {
        // ==========================================
        // با نسبت قفل شده: فقط عرض را تغییر بده
        // ==========================================
        let proposedW;

        if (this.resizeHandle === 'se' || this.resizeHandle === 'ne') {
          // گوشه راست: عرض افزایش/کاهش
          proposedW = box.width + dx;
        } else {
          // گوشه چپ: عرض کاهش/افزایش
          proposedW = box.width - dx;
        }

        newW = Math.max(min, proposedW);
        newH = newW / ratio;

        // ==========================================
        // تعیین موقعیت X و Y
        // ==========================================
        if (this.resizeHandle === 'nw' || this.resizeHandle === 'sw') {
          newX = rightEdge - newW;
        } else {
          newX = box.x;
        }

        if (this.resizeHandle === 'nw' || this.resizeHandle === 'ne') {
          newY = bottomEdge - newH;
        } else {
          newY = box.y;
        }

        // ==========================================
        // محدودیت‌های کانتینر (با حفظ نسبت)
        // ==========================================
        // اگر از سمت راست بیرون زد
        if (newX + newW > this.imageDisplayWidth) {
          newW = this.imageDisplayWidth - newX;
          newH = newW / ratio;
          if (this.resizeHandle === 'nw' || this.resizeHandle === 'ne') {
            newY = bottomEdge - newH;
          }
        }

        // اگر از سمت پایین بیرون زد
        if (newY + newH > this.imageDisplayHeight) {
          newH = this.imageDisplayHeight - newY;
          newW = newH * ratio;
          if (this.resizeHandle === 'nw' || this.resizeHandle === 'sw') {
            newX = rightEdge - newW;
          }
        }

        // اگر از سمت چپ بیرون زد
        if (newX < 0) {
          const overflow = -newX;
          newX = 0;
          newW -= overflow;
          newH = newW / ratio;
          if (this.resizeHandle === 'nw' || this.resizeHandle === 'ne') {
            newY = bottomEdge - newH;
          }
        }

        // اگر از سمت بالا بیرون زد
        if (newY < 0) {
          const overflow = -newY;
          newY = 0;
          newH -= overflow;
          newW = newH * ratio;
          if (this.resizeHandle === 'nw' || this.resizeHandle === 'sw') {
            newX = rightEdge - newW;
          }
        }

      } else {
        // ==========================================
        // بدون نسبت قفل شده: هر دو بعد آزاد
        // ==========================================
        if (this.resizeHandle === 'se') {
          newW = box.width + dx;
          newH = box.height + dy;
        } else if (this.resizeHandle === 'sw') {
          newW = box.width - dx;
          newH = box.height + dy;
          newX = box.x + dx;
        } else if (this.resizeHandle === 'ne') {
          newW = box.width + dx;
          newH = box.height - dy;
          newY = box.y + dy;
        } else if (this.resizeHandle === 'nw') {
          newW = box.width - dx;
          newH = box.height - dy;
          newX = box.x + dx;
          newY = box.y + dy;
        }

        // حداقل اندازه
        newW = Math.max(min, newW);
        newH = Math.max(min, newH);

        // محدودیت‌ها
        if (newX < 0) { newW += newX; newX = 0; }
        if (newY < 0) { newH += newY; newY = 0; }
        if (newX + newW > this.imageDisplayWidth) newW = this.imageDisplayWidth - newX;
        if (newY + newH > this.imageDisplayHeight) newH = this.imageDisplayHeight - newY;
      }

      this.cropBox = { x: newX, y: newY, width: newW, height: newH };
    },

    stopResize() {
      this.isResizing = false;
      this.resizeHandle = null;
    },

    // ==========================================
    // رویدادهای گلوبال
    // ==========================================
    bindGlobalEvents() {
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.stopDrag);
      window.addEventListener('mousemove', this.onResize);
      window.addEventListener('mouseup', this.stopResize);
      window.addEventListener('touchmove', this.onDrag, { passive: false });
      window.addEventListener('touchend', this.stopDrag);
      window.addEventListener('touchmove', this.onResize, { passive: false });
      window.addEventListener('touchend', this.stopResize);
    },

    unbindGlobalEvents() {
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.stopDrag);
      window.removeEventListener('mousemove', this.onResize);
      window.removeEventListener('mouseup', this.stopResize);
      window.removeEventListener('touchmove', this.onDrag);
      window.removeEventListener('touchend', this.stopDrag);
      window.removeEventListener('touchmove', this.onResize);
      window.removeEventListener('touchend', this.stopResize);
    },

    // ==========================================
    // محاسبه ابعاد خروجی (با حفظ نسبت)
    // ==========================================
    calculateOutputSize() {
      if (!this.cropBox.width || !this.imageNaturalWidth || !this.imageDisplayWidth) {
        return { width: 0, height: 0 };
      }

      const scaleX = this.imageNaturalWidth / this.imageDisplayWidth;
      const scaleY = this.imageNaturalHeight / this.imageDisplayHeight;

      let outW = Math.round(this.cropBox.width * scaleX);
      let outH = Math.round(this.cropBox.height * scaleY);

      // اعمال maxWidth/maxHeight با حفظ نسبت
      if (this.maxWidth && outW > this.maxWidth) {
        outH = Math.round(outH * (this.maxWidth / outW));
        outW = this.maxWidth;
      }
      if (this.maxHeight && outH > this.maxHeight) {
        outW = Math.round(outW * (this.maxHeight / outH));
        outH = this.maxHeight;
      }

      return { width: outW, height: outH };
    },
    // ==========================================
    // تایید و کراپ نهایی
    // ==========================================
    confirm() {
      const img = this.$refs.sourceImage;
      if (!img) return;

      // ==========================================
      // مقیاس: تصویر اصلی / تصویر نمایشی
      // ==========================================
      const scaleX = this.imageNaturalWidth / this.imageDisplayWidth;
      const scaleY = this.imageNaturalHeight / this.imageDisplayHeight;

      // ==========================================
      // مختصات در تصویر اصلی
      // (cropBox.x نسبت به تصویر است، نه کانتینر)
      // ==========================================
      const sx = this.cropBox.x * scaleX;
      const sy = this.cropBox.y * scaleY;
      const sw = this.cropBox.width * scaleX;
      const sh = this.cropBox.height * scaleY;

      // ==========================================
      // ابعاد خروجی با حفظ نسبت
      // ==========================================
      const size = this.calculateOutputSize();

      const canvas = document.createElement('canvas');
      canvas.width = size.width;
      canvas.height = size.height;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
          img,
          sx, sy, sw, sh,
          0, 0, size.width, size.height
      );

      // ==========================================
      // خروجی Base64
      // ==========================================
      let base64;
      const testCanvas = document.createElement('canvas');
      testCanvas.width = 1;
      testCanvas.height = 1;
      const testData = testCanvas.toDataURL('image/webp');

      if (testData.indexOf('data:image/webp') === 0) {
        base64 = canvas.toDataURL('image/webp', 0.9);
      } else {
        base64 = canvas.toDataURL('image/jpeg', 0.9);
      }

      this.$emit('confirm', {
        base64: base64,
        width: size.width,
        height: size.height
      });
    },
    cancel() {
      this.$emit('cancel');
    },

    // ==========================================
    // کمکی
    // ==========================================
    getPoint(e) {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    },

    clamp(val, min, max) {
      return Math.min(Math.max(val, min), max);
    }
  }
};
</script>

<style scoped>
.crop-modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
  direction: rtl;
}

.crop-modal-content {
  background: white;
  border-radius: 14px;
  max-width: 800px;
  width: 100%;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.crop-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.crop-modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 17px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #909399;
  line-height: 1;
}

.crop-modal-body {
  padding: 16px 20px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.crop-info-bar {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.crop-info {
  background: #f0f9eb;
  color: #67c23a;
  padding: 3px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 12px;
}

.crop-container {
  position: relative;
  width: 100%;
  height: 400px;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  user-select: none;
  touch-action: none;
}

.crop-source-image {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
}

.crop-box {
  position: absolute;
  border: 2px solid white;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  cursor: move;
  box-sizing: border-box;
  z-index: 2;
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.01);
  touch-action: none;
}

.crop-handle {
  position: absolute;
  width: 16px;
  height: 16px;
  background: white;
  border: 2px solid #409eff;
  border-radius: 50%;
  z-index: 3;
  touch-action: none;
}

.handle-nw { top: -8px; left: -8px; cursor: nwse-resize; }
.handle-ne { top: -8px; right: -8px; cursor: nesw-resize; }
.handle-sw { bottom: -8px; left: -8px; cursor: nesw-resize; }
.handle-se { bottom: -8px; right: -8px; cursor: nwse-resize; }

.crop-grid {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
}

.grid-line {
  position: absolute;
  background: rgba(255, 255, 255, 0.3);
}

.grid-v1 { left: 33.33%; top: 0; bottom: 0; width: 1px; }
.grid-v2 { left: 66.66%; top: 0; bottom: 0; width: 1px; }
.grid-h1 { top: 33.33%; left: 0; right: 0; height: 1px; }
.grid-h2 { top: 66.66%; left: 0; right: 0; height: 1px; }

.crop-controls {
  display: flex;
  justify-content: center;
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f7fa;
  padding: 6px 12px;
  border-radius: 20px;
}

.btn-zoom {
  width: 34px;
  height: 34px;
  border: none;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.btn-zoom:hover { background: #ecf5ff; color: #409eff; }
.btn-reset-zoom { margin-right: 8px; font-size: 16px; }

.zoom-value {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  min-width: 45px;
  text-align: center;
}

.crop-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 10px 24px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-confirm {
  padding: 10px 24px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.btn-confirm:hover { background: #66b1ff; }

@media (max-width: 768px) {
  .crop-container { height: 300px; }
  .crop-modal-content { max-height: 98vh; }
  .crop-modal-body { padding: 12px; }
  .crop-modal-footer { padding: 12px 16px; flex-direction: column; }
  .btn-cancel, .btn-confirm { width: 100%; }
}
</style>